import { z } from "zod";

/**
 * Block-based email document — the design behind the drag-and-drop builder.
 * Stored as JSON on templates and sends; compiled to table-based, inline-styled
 * HTML that email clients tolerate. Merge fields ({{firstName}}, {{name}},
 * {{amount}}, {{date}}) survive compilation and are filled per-recipient at
 * send time.
 */

export const zEmailRichText = z.array(z.array(z.object({
  text: z.string().max(20_000),
  bold: z.boolean().optional(),
  italic: z.boolean().optional(),
  underline: z.boolean().optional(),
  href: z.string().max(1000).optional(),
})).max(500)).max(200);
export type EmailRichText = z.infer<typeof zEmailRichText>;

export const zEmailBlock = z.discriminatedUnion("type", [
  z.object({
    id: z.string(),
    type: z.literal("heading"),
    text: z.string().max(300),
    level: z.union([z.literal(1), z.literal(2)]).default(1),
    align: z.enum(["left", "center", "right"]).default("left"),
  }),
  z.object({
    id: z.string(),
    type: z.literal("text"),
    richText: zEmailRichText.optional(),
    text: z.string().max(20_000).describe("plain text; blank lines separate paragraphs; **bold** and [label](url) links supported"),
    align: z.enum(["left", "center", "right"]).default("left"),
  }),
  z.object({
    id: z.string(),
    type: z.literal("image"),
    src: z.string().max(1000),
    alt: z.string().max(300).default(""),
    width: z.number().int().min(10).max(100).default(100).describe("percent of content width"),
    align: z.enum(["left", "center", "right"]).default("center"),
  }),
  z.object({
    id: z.string(),
    type: z.literal("button"),
    label: z.string().max(100),
    url: z.string().max(1000),
    align: z.enum(["left", "center", "right"]).default("center"),
  }),
  z.object({
    id: z.string(),
    type: z.literal("divider"),
  }),
  z.object({
    id: z.string(),
    type: z.literal("spacer"),
    height: z.number().int().min(4).max(120).default(24),
  }),
  z.object({
    id: z.string(),
    type: z.literal("giftAsk"),
    heading: z.string().max(200).default("Make a gift today"),
    amounts: z.array(z.number().int().min(100)).min(1).max(6).default([2500, 5000, 10000, 25000]),
    url: z.string().max(1000).describe("giving page URL; ?amount=<cents> is appended per button"),
  }),
]);
export type EmailBlock = z.infer<typeof zEmailBlock>;

export const zEmailDocStyles = z.object({
  backgroundColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).default("#f4f2ee"),
  contentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).default("#ffffff"),
  textColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).default("#26241f"),
  accentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).default("#0e7a4d"),
  fontFamily: z
    .enum(["Georgia, 'Times New Roman', serif", "Arial, Helvetica, sans-serif", "'Trebuchet MS', Verdana, sans-serif"])
    .default("Arial, Helvetica, sans-serif"),
});
export type EmailDocStyles = z.infer<typeof zEmailDocStyles>;

export const zEmailDoc = z.object({
  version: z.literal(1),
  preheader: z.string().max(180).optional(),
  styles: zEmailDocStyles,
  blocks: z.array(zEmailBlock).max(60),
});
export type EmailDoc = z.infer<typeof zEmailDoc>;

const safeUrl = (value: string) => { try { const url = new URL(value); return ["https:", "http:"].includes(url.protocol) ? url.href : ""; } catch { return ""; } };

const esc = (s: string) =>
  s.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;");

/** minimal inline markup: **bold** and [label](url), preserving merge fields */
function inline(text: string, accent: string): string {
  return esc(text)
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, `<a href="$2" style="color:${accent};text-decoration:underline">$1</a>`);
}

function giftUrl(value: string, amount: number): string {
  const safe = safeUrl(value); if (!safe) return "";
  const url = new URL(safe); url.searchParams.set("amount", String(amount)); return url.href;
}

export function renderEmailText(block: Extract<EmailBlock, { type: "text" }>, accent: string): string {
  if (block.richText) return block.richText.map(paragraph => `<p>${paragraph.map(run => {
    let content = esc(run.text).replaceAll("\n", "<br>");
    if (run.bold) content = `<strong>${content}</strong>`;
    if (run.italic) content = `<em>${content}</em>`;
    if (run.underline) content = `<u>${content}</u>`;
    if (run.href && safeUrl(run.href)) content = `<a href="${esc(safeUrl(run.href))}" style="color:${accent};text-decoration:underline">${content}</a>`;
    return content;
  }).join("") || "<br>"}</p>`).join("");
  return block.text.split(/\n\s*\n/).map(text => `<p>${inline(text, accent).replaceAll("\n", "<br>")}</p>`).join("");
}

const fmtUsdShort = (cents: number) => `$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

function renderBlock(block: EmailBlock, s: EmailDocStyles): string {
  switch (block.type) {
    case "heading": {
      const size = block.level === 1 ? 26 : 20;
      return `<tr><td style="padding:8px 32px;text-align:${block.align}"><div style="font-size:${size}px;line-height:1.25;font-weight:bold;color:${s.textColor}">${inline(block.text, s.accentColor)}</div></td></tr>`;
    }
    case "text": {
      const paragraphs = renderEmailText(block, s.accentColor).replaceAll("<p>", `<p style="margin:0 0 12px;font-size:15px;line-height:1.55;color:${s.textColor}">`);
      return `<tr><td style="padding:4px 32px;text-align:${block.align}">${paragraphs}</td></tr>`;
    }
    case "image":
      return `<tr><td style="padding:8px 32px;text-align:${block.align}"><img src="${esc(safeUrl(block.src))}" alt="${esc(block.alt)}" width="${Math.round(536 * (block.width / 100))}" border="0" style="max-width:100%;height:auto;border-radius:6px" /></td></tr>`;
    case "button":
      return `<tr><td style="padding:12px 32px;text-align:${block.align}"><a href="${esc(safeUrl(block.url))}" style="display:inline-block;background:${s.accentColor};color:#ffffff;font-size:15px;font-weight:bold;text-decoration:none;padding:12px 28px;border-radius:8px">${esc(block.label)}</a></td></tr>`;
    case "divider":
      return `<tr><td style="padding:12px 32px"><div style="border-top:1px solid #d9d5cd;font-size:0;line-height:0">&nbsp;</div></td></tr>`;
    case "spacer":
      return `<tr><td style="height:${block.height}px;font-size:0;line-height:0">&nbsp;</td></tr>`;
    case "giftAsk": {
      const buttons = block.amounts
        .map(
          (cents) =>
            `<a href="${esc(giftUrl(block.url, cents))}" style="display:inline-block;margin:4px;background:#ffffff;border:2px solid ${s.accentColor};color:${s.accentColor};font-size:15px;font-weight:bold;text-decoration:none;padding:10px 20px;border-radius:8px">${fmtUsdShort(cents)}</a>`,
        )
        .join("");
      return `<tr><td style="padding:12px 32px;text-align:center"><div style="font-size:17px;font-weight:bold;margin-bottom:8px;color:${s.textColor}">${inline(block.heading, s.accentColor)}</div>${buttons}</td></tr>`;
    }
  }
}

/** Compiles a block document to email-client-safe HTML (600px table, inline styles). */
export function renderEmailDoc(doc: EmailDoc): string {
  const s = doc.styles;
  const rows = doc.blocks.map((b) => renderBlock(b, s)).join("\n");
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"></head><body style="margin:0;padding:0;background:${s.backgroundColor}">
${doc.preheader ? `<div style="display:none;max-height:0;overflow:hidden;opacity:0;mso-hide:all">${esc(doc.preheader)}</div>` : ""}
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${s.backgroundColor};font-family:${s.fontFamily}">
<tr><td align="center" style="padding:24px 12px">
<table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:${s.contentColor};border-radius:10px;overflow:hidden">
<tr><td style="height:20px;font-size:0">&nbsp;</td></tr>
${rows}
<tr><td style="height:24px;font-size:0">&nbsp;</td></tr>
</table>
</td></tr>
</table>
</body></html>`;
}

export const zEmailTemplateInput = z.object({
  name: z.string().trim().min(1).max(200),
  subject: z.string().trim().min(1).max(300),
  bodyHtml: z.string().min(1).max(200_000),
  kind: z.string().max(40).default("general"),
  design: zEmailDoc.nullable().optional(),
  expectedUpdatedAt: z.string().datetime().optional(),
});

export const zEmailSendInput = z.object({
  name: z.string().trim().min(1).max(200),
  segmentId: z.string().uuid(),
  subject: z.string().trim().min(1).max(300),
  bodyHtml: z.string().min(1).max(200_000),
  design: zEmailDoc.nullable().optional(),
});

export interface EmailTemplateRecord { id: string; name: string; subject: string; bodyHtml: string; design: EmailDoc | null; updatedAt: string }
